import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { ArrowLeft, Save, Send, FileText, Sparkles, Loader2, Eye, CheckCircle2 } from 'lucide-react';

const TEMPLATE_FIELDS = {
  SURVEILLANCE: [
    { key: 'subject_name', label: 'Subject Name', type: 'text' },
    { key: 'surveillance_dates', label: 'Surveillance Dates', type: 'text' },
    { key: 'surveillance_times', label: 'Surveillance Times', type: 'text' },
    { key: 'locations_observed', label: 'Locations Observed', type: 'textarea' },
    { key: 'activities_documented', label: 'Activities Documented', type: 'textarea' },
    { key: 'vehicles_observed', label: 'Vehicles Observed', type: 'textarea' },
    { key: 'persons_contacted', label: 'Persons Subject Contacted', type: 'textarea' },
    { key: 'photographs_taken', label: 'Photographs/Video Description', type: 'textarea' },
    { key: 'subject_behavior', label: 'Subject Behavior Observations', type: 'textarea' },
  ],
  BACKGROUND_CHECK: [
    { key: 'subject_full_name', label: 'Subject Full Legal Name', type: 'text' },
    { key: 'date_of_birth', label: 'Date of Birth', type: 'text' },
    { key: 'social_security', label: 'SSN (Last 4 digits)', type: 'text' },
    { key: 'current_address', label: 'Current Address', type: 'text' },
    { key: 'previous_addresses', label: 'Previous Addresses', type: 'textarea' },
    { key: 'employment_history', label: 'Employment History', type: 'textarea' },
    { key: 'education_verification', label: 'Education Verification', type: 'textarea' },
    { key: 'criminal_history', label: 'Criminal History', type: 'textarea' },
    { key: 'civil_records', label: 'Civil Court Records', type: 'textarea' },
    { key: 'credit_summary', label: 'Credit Summary', type: 'textarea' },
    { key: 'references_contacted', label: 'References Contacted', type: 'textarea' },
  ],
  INSURANCE_FRAUD: [
    { key: 'claimant_name', label: 'Claimant Name', type: 'text' },
    { key: 'claim_number', label: 'Claim Number', type: 'text' },
    { key: 'insurance_company', label: 'Insurance Company', type: 'text' },
    { key: 'type_of_claim', label: 'Type of Claim', type: 'text' },
    { key: 'alleged_injury', label: 'Alleged Injury/Damage', type: 'textarea' },
    { key: 'surveillance_findings', label: 'Surveillance Findings', type: 'textarea' },
    { key: 'inconsistencies_noted', label: 'Inconsistencies Noted', type: 'textarea' },
    { key: 'witness_statements', label: 'Witness Statements', type: 'textarea' },
    { key: 'evidence_of_fraud', label: 'Evidence of Fraud', type: 'textarea' },
    { key: 'recommendation', label: 'Recommendation', type: 'textarea' },
  ],
  SKIP_TRACE: [
    { key: 'subject_name', label: 'Subject Name', type: 'text' },
    { key: 'last_known_address', label: 'Last Known Address', type: 'text' },
    { key: 'reason_for_locate', label: 'Reason for Locate', type: 'text' },
    { key: 'databases_searched', label: 'Databases Searched', type: 'textarea' },
    { key: 'addresses_checked', label: 'Addresses Checked', type: 'textarea' },
    { key: 'contacts_interviewed', label: 'Contacts Interviewed', type: 'textarea' },
    { key: 'current_location', label: 'Current Location Found', type: 'textarea' },
    { key: 'employment_info', label: 'Employment Information', type: 'textarea' },
    { key: 'vehicle_info', label: 'Vehicle Information', type: 'textarea' },
  ],
  ASSET_SEARCH: [
    { key: 'subject_name', label: 'Subject Name', type: 'text' },
    { key: 'purpose_of_search', label: 'Purpose of Search', type: 'text' },
    { key: 'real_property', label: 'Real Property Holdings', type: 'textarea' },
    { key: 'vehicles_boats', label: 'Vehicles/Boats/Aircraft', type: 'textarea' },
    { key: 'business_interests', label: 'Business Interests', type: 'textarea' },
    { key: 'bank_accounts', label: 'Bank Accounts Identified', type: 'textarea' },
    { key: 'investments', label: 'Investments/Securities', type: 'textarea' },
    { key: 'liens_judgments', label: 'Liens and Judgments', type: 'textarea' },
    { key: 'estimated_net_worth', label: 'Estimated Net Worth', type: 'text' },
  ],
  DOMESTIC: [
    { key: 'subject_name', label: 'Subject Name', type: 'text' },
    { key: 'relationship_to_client', label: 'Relationship to Client', type: 'text' },
    { key: 'investigation_dates', label: 'Investigation Dates', type: 'text' },
    { key: 'living_situation', label: 'Living Situation', type: 'textarea' },
    { key: 'daily_activities', label: 'Daily Activities Observed', type: 'textarea' },
    { key: 'associates_identified', label: 'Associates Identified', type: 'textarea' },
    { key: 'behavioral_observations', label: 'Behavioral Observations', type: 'textarea' },
    { key: 'evidence_collected', label: 'Evidence Collected', type: 'textarea' },
  ],
  BAIL_BOND: [
    { key: 'defendant_name', label: 'Defendant Name', type: 'text' },
    { key: 'case_number', label: 'Court Case Number', type: 'text' },
    { key: 'bond_amount', label: 'Bond Amount', type: 'text' },
    { key: 'court_date_missed', label: 'Court Date Missed', type: 'text' },
    { key: 'charges', label: 'Original Charges', type: 'textarea' },
    { key: 'last_known_address', label: 'Last Known Address', type: 'text' },
    { key: 'known_associates', label: 'Known Associates', type: 'textarea' },
    { key: 'locations_checked', label: 'Locations Checked', type: 'textarea' },
    { key: 'apprehension_details', label: 'Apprehension Details', type: 'textarea' },
    { key: 'surrender_location', label: 'Surrender Location', type: 'text' },
  ],
  STOLEN_PROPERTY: [
    { key: 'property_description', label: 'Property Description', type: 'textarea' },
    { key: 'estimated_value', label: 'Estimated Value', type: 'text' },
    { key: 'date_stolen', label: 'Date Stolen', type: 'text' },
    { key: 'location_stolen', label: 'Location Stolen From', type: 'text' },
    { key: 'police_report_number', label: 'Police Report Number', type: 'text' },
    { key: 'investigation_methods', label: 'Investigation Methods', type: 'textarea' },
    { key: 'leads_followed', label: 'Leads Followed', type: 'textarea' },
    { key: 'recovery_details', label: 'Recovery Details', type: 'textarea' },
    { key: 'suspects_identified', label: 'Suspects Identified', type: 'textarea' },
  ],
  DIVORCE: [
    { key: 'spouse_name', label: 'Spouse Under Investigation', type: 'text' },
    { key: 'client_spouse_name', label: 'Client Name', type: 'text' },
    { key: 'marriage_date', label: 'Date of Marriage', type: 'text' },
    { key: 'investigation_purpose', label: 'Purpose of Investigation', type: 'textarea' },
    { key: 'surveillance_summary', label: 'Surveillance Summary', type: 'textarea' },
    { key: 'financial_findings', label: 'Financial Findings', type: 'textarea' },
    { key: 'asset_discoveries', label: 'Asset Discoveries', type: 'textarea' },
    { key: 'behavioral_observations', label: 'Behavioral Observations', type: 'textarea' },
    { key: 'evidence_for_court', label: 'Evidence for Court', type: 'textarea' },
  ],
  CHILD_CUSTODY: [
    { key: 'child_name', label: 'Child Name(s)', type: 'text' },
    { key: 'child_age', label: 'Child Age(s)', type: 'text' },
    { key: 'custodial_parent', label: 'Custodial Parent', type: 'text' },
    { key: 'non_custodial_parent', label: 'Non-Custodial Parent', type: 'text' },
    { key: 'observation_dates', label: 'Observation Dates', type: 'text' },
    { key: 'living_conditions', label: 'Living Conditions Observed', type: 'textarea' },
    { key: 'child_behavior', label: 'Child Behavior Observations', type: 'textarea' },
    { key: 'parental_conduct', label: 'Parental Conduct', type: 'textarea' },
    { key: 'safety_concerns', label: 'Safety Concerns', type: 'textarea' },
    { key: 'visitation_compliance', label: 'Visitation Compliance', type: 'textarea' },
    { key: 'recommendations', label: 'Recommendations', type: 'textarea' },
  ],
  INFIDELITY: [
    { key: 'subject_name', label: 'Subject Name', type: 'text' },
    { key: 'relationship_to_client', label: 'Relationship to Client', type: 'text' },
    { key: 'surveillance_dates', label: 'Surveillance Dates', type: 'text' },
    { key: 'surveillance_locations', label: 'Surveillance Locations', type: 'textarea' },
    { key: 'persons_contacted', label: 'Persons Subject Contacted', type: 'textarea' },
    { key: 'suspicious_activity', label: 'Suspicious Activity Observed', type: 'textarea' },
    { key: 'meetings_documented', label: 'Meetings Documented', type: 'textarea' },
    { key: 'evidence_collected', label: 'Evidence Collected', type: 'textarea' },
    { key: 'conclusions', label: 'Conclusions', type: 'textarea' },
  ],
  WORKERS_COMP: [
    { key: 'claimant_name', label: 'Claimant Name', type: 'text' },
    { key: 'claim_number', label: 'Workers Comp Claim Number', type: 'text' },
    { key: 'employer', label: 'Employer', type: 'text' },
    { key: 'alleged_injury', label: 'Alleged Injury', type: 'textarea' },
    { key: 'injury_date', label: 'Date of Injury', type: 'text' },
    { key: 'restrictions_claimed', label: 'Restrictions Claimed', type: 'textarea' },
    { key: 'surveillance_summary', label: 'Surveillance Summary', type: 'textarea' },
    { key: 'activities_observed', label: 'Physical Activities Observed', type: 'textarea' },
    { key: 'inconsistencies', label: 'Inconsistencies with Claimed Injury', type: 'textarea' },
    { key: 'sub_rosa_video', label: 'Sub Rosa Video Description', type: 'textarea' },
  ],
  DOMESTIC_VIOLENCE: [
    { key: 'victim_name', label: 'Victim Name', type: 'text' },
    { key: 'alleged_abuser', label: 'Alleged Abuser', type: 'text' },
    { key: 'relationship', label: 'Relationship', type: 'text' },
    { key: 'incident_dates', label: 'Incident Dates', type: 'text' },
    { key: 'incident_description', label: 'Incident Description', type: 'textarea' },
    { key: 'injuries_documented', label: 'Injuries Documented', type: 'textarea' },
    { key: 'witnesses', label: 'Witnesses', type: 'textarea' },
    { key: 'police_involvement', label: 'Police Involvement', type: 'textarea' },
    { key: 'protective_orders', label: 'Protective Orders', type: 'textarea' },
    { key: 'safety_plan', label: 'Safety Plan Recommendations', type: 'textarea' },
  ],
  MISSING_PERSON: [
    { key: 'missing_person_name', label: 'Missing Person Name', type: 'text' },
    { key: 'age', label: 'Age', type: 'text' },
    { key: 'last_seen_date', label: 'Date Last Seen', type: 'text' },
    { key: 'last_known_location', label: 'Last Known Location', type: 'text' },
    { key: 'physical_description', label: 'Physical Description', type: 'textarea' },
    { key: 'circumstances', label: 'Circumstances of Disappearance', type: 'textarea' },
    { key: 'leads_investigated', label: 'Leads Investigated', type: 'textarea' },
    { key: 'witnesses_interviewed', label: 'Witnesses Interviewed', type: 'textarea' },
    { key: 'search_areas', label: 'Search Areas Covered', type: 'textarea' },
    { key: 'findings', label: 'Findings', type: 'textarea' },
    { key: 'current_status', label: 'Current Status', type: 'textarea' },
  ],
  PROCESS_SERVING: [
    { key: 'defendant_name', label: 'Defendant Name', type: 'text' },
    { key: 'case_caption', label: 'Case Caption', type: 'text' },
    { key: 'court', label: 'Court', type: 'text' },
    { key: 'documents_served', label: 'Documents Served', type: 'textarea' },
    { key: 'service_address', label: 'Service Address', type: 'text' },
    { key: 'service_date', label: 'Date of Service', type: 'text' },
    { key: 'service_time', label: 'Time of Service', type: 'text' },
    { key: 'service_method', label: 'Method of Service', type: 'text' },
    { key: 'person_served', label: 'Person Served (if substitute)', type: 'text' },
    { key: 'physical_description', label: 'Physical Description of Person Served', type: 'textarea' },
    { key: 'attempts_made', label: 'Previous Attempts Made', type: 'textarea' },
  ],
  GENERAL: [
    { key: 'subject_of_investigation', label: 'Subject of Investigation', type: 'text' },
    { key: 'client_objective', label: 'Client Objective', type: 'textarea' },
    { key: 'investigation_scope', label: 'Investigation Scope', type: 'textarea' },
    { key: 'methods_used', label: 'Methods Used', type: 'textarea' },
    { key: 'timeline', label: 'Investigation Timeline', type: 'textarea' },
    { key: 'key_findings', label: 'Key Findings', type: 'textarea' },
    { key: 'evidence_summary', label: 'Evidence Summary', type: 'textarea' },
    { key: 'conclusions', label: 'Conclusions', type: 'textarea' },
    { key: 'recommendations', label: 'Recommendations', type: 'textarea' },
  ],
};

const REPORT_TYPE_NAMES = {
  SURVEILLANCE: 'Surveillance Report',
  BACKGROUND_CHECK: 'Background Check Report',
  INSURANCE_FRAUD: 'Insurance Fraud Investigation Report',
  SKIP_TRACE: 'Skip Trace Report',
  ASSET_SEARCH: 'Asset Search Report',
  DOMESTIC: 'Domestic Investigation Report',
  BAIL_BOND: 'Bail Bond Apprehension Report',
  STOLEN_PROPERTY: 'Stolen Property Recovery Report',
  DIVORCE: 'Divorce Investigation Report',
  CHILD_CUSTODY: 'Child Custody Investigation Report',
  INFIDELITY: 'Infidelity Investigation Report',
  WORKERS_COMP: 'Workers Compensation Investigation Report',
  DOMESTIC_VIOLENCE: 'Domestic Violence Investigation Report',
  MISSING_PERSON: 'Missing Person Investigation Report',
  PROCESS_SERVING: 'Process Serving Report',
  GENERAL: 'General Investigation Report',
};

const CreateReport = () => {
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const editReportId = searchParams.get('edit');
  const viewReportId = searchParams.get('view');
  const readOnlyMode = !!viewReportId;
  const loadReportId = editReportId || viewReportId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('nexus_token');

  const [caseData, setCaseData] = useState(null);
  const [reportTypes, setReportTypes] = useState([]);
  const [reportType, setReportType] = useState('');
  const [title, setTitle] = useState('');
  const [formData, setFormData] = useState({});
  const [narrative, setNarrative] = useState('');
  const [signature, setSignature] = useState('');
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [existingReport, setExistingReport] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [caseRes, typesRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${BACKEND_URL}/api/report-types`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (caseRes.ok) setCaseData(await caseRes.json());
        if (typesRes.ok) setReportTypes(await typesRes.json());

        if (loadReportId) {
          const repRes = await fetch(`${BACKEND_URL}/api/reports/${loadReportId}`, { headers: { Authorization: `Bearer ${token}` } });
          if (repRes.ok) {
            const rep = await repRes.json();
            setExistingReport(rep);
            setReportType(rep.report_type);
            setTitle(rep.title);
            setFormData(rep.data || {});
            setNarrative(rep.narrative || '');
            setSignature(rep.signature || '');
            setSignatureDate(rep.signature_date || new Date().toISOString().split('T')[0]);
          }
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, [caseId, loadReportId, token]);

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerateNarrative = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/generate-narrative`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          report_type: reportType,
          form_data: formData,
          case_title: caseData?.title,
          case_type: caseData?.case_type
        })
      });
      if (res.ok) {
        const data = await res.json();
        setNarrative(data.narrative);
        toast({ title: 'AI Narrative Generated', description: 'Professional narrative has been generated from your form data' });
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Generation failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const [attachingEvidence, setAttachingEvidence] = useState(false);

  const handleGenerateAndAttach = async () => {
    if (!caseId) {
      toast({ title: 'No case selected', description: 'Cannot attach to a case until one is linked.', variant: 'destructive' });
      return;
    }
    setAttachingEvidence(true);
    try {
      // Generate narrative first if empty
      let narrativeText = narrative;
      if (!narrativeText?.trim()) {
        const genRes = await fetch(`${BACKEND_URL}/api/ai/generate-narrative`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ report_type: reportType, form_data: formData, case_title: caseData?.title, case_type: caseData?.case_type })
        });
        if (!genRes.ok) {
          const err = await genRes.json().catch(() => ({}));
          throw new Error(err.detail || 'Could not generate narrative');
        }
        const genData = await genRes.json();
        narrativeText = genData.narrative;
        setNarrative(narrativeText);
      }
      // Attach PDF to evidence
      const res = await fetch(`${BACKEND_URL}/api/ai/narrative-to-evidence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ case_id: caseId, narrative: narrativeText, report_type: reportType, description: `AI narrative for "${title || 'untitled report'}"` })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Attach failed (${res.status})`);
      }
      const ev = await res.json();
      toast({ title: 'Attached as Evidence', description: `PDF saved to the case evidence tab (ID: ${ev.id})` });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setAttachingEvidence(false);
    }
  };

  const handleSave = async (andSubmit = false) => {
    if (!reportType || !title) {
      toast({ title: 'Error', description: 'Report type and title are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        case_id: caseId,
        report_type: reportType,
        title,
        data: formData,
        narrative,
        signature: signature || null,
        signature_date: signatureDate || null,
      };

      let reportId = existingReport?.id;

      if (existingReport) {
        await fetch(`${BACKEND_URL}/api/reports/${existingReport.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body)
        });
      } else {
        const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/reports`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body)
        });
        if (res.ok) {
          const data = await res.json();
          reportId = data.id;
        }
      }

      if (andSubmit && reportId) {
        await fetch(`${BACKEND_URL}/api/reports/${reportId}/submit`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Success', description: 'Report saved and submitted' });
      } else {
        toast({ title: 'Success', description: 'Report saved as draft' });
      }
      navigate(`/cases/${caseId}`);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const fields = TEMPLATE_FIELDS[reportType] || TEMPLATE_FIELDS.GENERAL;

  return (
    <div className="space-y-6 nx-form-with-action-bar" data-testid="create-report-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to={`/cases/${caseId}`} className="inline-flex items-center gap-1 text-slate-400 hover:text-white mb-3 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Case
          </Link>
          <h1 className="text-2xl font-bold text-white">{readOnlyMode ? 'View Report' : (existingReport ? 'Edit Report' : 'Create Report')}</h1>
          {caseData && <p className="text-slate-400 text-sm">{caseData.case_number} - {caseData.title}</p>}
          {readOnlyMode && (
            <p className="text-xs text-amber-300 mt-1 flex items-center gap-1">
              <Eye className="w-3 h-3" /> Read-only view. {existingReport?.status === 'APPROVED' ? 'Report has been approved — no further edits allowed.' : 'Only the author can edit this report until it is approved by an admin.'}
            </p>
          )}
        </div>
        {existingReport && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`${existingReport.status === 'APPROVED' ? 'bg-green-500' : existingReport.status === 'SUBMITTED' ? 'bg-blue-500' : 'bg-slate-500'} text-white`}>
              {existingReport.status}
            </Badge>
            {(user?.role === 'main_hub' || user?.role === 'department_admin') &&
              existingReport.status === 'SUBMITTED' && (
              <Button
                size="sm"
                onClick={async () => {
                  if (!window.confirm('Approve this report? Once approved, no further edits are allowed.')) return;
                  try {
                    const res = await fetch(`${BACKEND_URL}/api/reports/${existingReport.id}/approve`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (res.ok) {
                      toast({ title: 'Report approved', description: 'Report is now locked and archived.' });
                      navigate(`/cases/${caseId}`);
                    } else {
                      const err = await res.json().catch(() => ({}));
                      toast({ title: 'Approval failed', description: err.detail || 'Server error', variant: 'destructive' });
                    }
                  } catch (e) {
                    toast({ title: 'Error', description: e.message, variant: 'destructive' });
                  }
                }}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                data-testid="approve-report-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve Report
              </Button>
            )}
          </div>
        )}
      </div>

      {readOnlyMode && (
        <style>{`
          [data-testid="create-report-page"] input,
          [data-testid="create-report-page"] textarea,
          [data-testid="create-report-page"] button[type="submit"],
          [data-testid="create-report-page"] [role="combobox"],
          [data-testid="create-report-page"] [data-testid="save-report-btn"],
          [data-testid="create-report-page"] [data-testid="submit-report-btn"],
          [data-testid="create-report-page"] [data-testid="generate-narrative-btn"] {
            pointer-events: none;
            opacity: 0.75;
          }
        `}</style>
      )}

      {/* Report Type & Title */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader><CardTitle className="text-white text-base">Report Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Report Type *</Label>
              <Select value={reportType} onValueChange={(v) => { setReportType(v); if (!title) setTitle(REPORT_TYPE_NAMES[v] || v); }}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="report-type-select">
                  <SelectValue placeholder="Select report type..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(REPORT_TYPE_NAMES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Report Title *</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)}
                className="bg-slate-900 border-slate-600 text-white" placeholder="Report title" data-testid="report-title-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Fields */}
      {reportType && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-400" /> {REPORT_TYPE_NAMES[reportType] || 'Report'} Fields
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {fields.map(field => (
              <div key={field.key}>
                <Label className="text-slate-300">{field.label}</Label>
                {field.type === 'textarea' ? (
                  <Textarea
                    value={formData[field.key] || ''}
                    onChange={e => handleFieldChange(field.key, e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white"
                    rows={3}
                    data-testid={`field-${field.key}`}
                  />
                ) : (
                  <Input
                    value={formData[field.key] || ''}
                    onChange={e => handleFieldChange(field.key, e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white"
                    data-testid={`field-${field.key}`}
                  />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Narrative */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-white text-base">Professional Narrative</CardTitle>
            {reportType && Object.values(formData).some(v => v) && (
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={handleGenerateNarrative} 
                  disabled={generating || attachingEvidence}
                  variant="outline" 
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  data-testid="generate-narrative-btn"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {generating ? 'Generating...' : 'AI Generate Narrative'}
                </Button>
                {caseId && (
                  <Button
                    onClick={handleGenerateAndAttach}
                    disabled={generating || attachingEvidence}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    data-testid="generate-attach-evidence-btn"
                    title="Generate the narrative, render it as a PDF, and attach it to this case's evidence tab"
                  >
                    {attachingEvidence ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {attachingEvidence ? 'Attaching...' : 'Generate + Attach as Evidence'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            value={narrative}
            onChange={e => setNarrative(e.target.value)}
            className="bg-slate-900 border-slate-600 text-white"
            rows={8}
            placeholder="Write the professional narrative summary of your investigation findings..."
            data-testid="report-narrative"
          />
        </CardContent>
      </Card>

      {/* Signature */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader><CardTitle className="text-white text-base">Digital Signature</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Signed By</Label>
              <Input
                value={signature}
                onChange={e => setSignature(e.target.value)}
                className="bg-slate-900 border-slate-600 text-white"
                placeholder={user?.name || 'Your full name'}
                data-testid="report-signature"
              />
            </div>
            <div>
              <Label className="text-slate-300">Signature Date</Label>
              <Input type="date" value={signatureDate} onChange={e => setSignatureDate(e.target.value)}
                className="bg-slate-900 border-slate-600 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions — sticky on mobile so Cancel / Save Draft / Save & Submit
          always sit above the bottom nav and remain tappable. */}
      <div className="nx-form-action-bar flex justify-end gap-3" data-testid="report-action-bar">
        <Link to={`/cases/${caseId}`}>
          <Button variant="outline" className="border-slate-600 text-slate-300" data-testid="report-cancel-btn">{readOnlyMode ? 'Close' : 'Cancel'}</Button>
        </Link>
        {!readOnlyMode && (
          <>
            <Button onClick={() => handleSave(false)} disabled={saving} className="bg-slate-600 hover:bg-slate-500 text-white" data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button onClick={() => handleSave(true)} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-submit-btn">
              <Send className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save & Submit'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateReport;
